import React from "react";
import { observer } from "mobx-react";
import { createStyles, Divider, Grid, withStyles } from "@material-ui/core";
import Info from "@material-ui/icons/Info";
import locale from "@bonliva-crm/locale";
import { CustomerType, OrganizationType, RecruitingPortalType } from "@graphql";
import { Classes } from "@theme";

import { TabBadge, WithTooltip } from "@components";
import { Select, Text } from "@components/formx_item_form";
import { FormxDebugger, Switch } from "@components/formx";
import Body from "@components/typography/Body";
import Collapsible from "@components/forms/Collapsible";
import { FormTab } from "@components/forms/common/FormTab";
import FormSection from "@components/forms/common/FormSection";
import { TabFormLayout } from "@components/forms/TabFormLayout";
import CustomerSelector from "@containers/VirtualSelector/CustomerSelector/CustomerSelector";

import { CustomerEditorStore } from "./CustomerEditorStore";
import CustomerAOESettings from "./components/CustomerAOESettings";
import CustomerWorkshiftSpans from "./components/CustomerWorkshiftSpans";

type OwnProps = {
  store: CustomerEditorStore;
  onClose?: () => void;
};

type Props = OwnProps & {
  classes: Classes<typeof styles>;
};

@observer
class CustomerForm extends React.Component<Props> {
  renderMainTab = () => {
    const { classes, store } = this.props;

    return (
      <div className={classes.mainTab}>
        {store.showOverwriteWarning && (
          <FormSection fullWidth>
            <Body style={{ fontWeight: "bold" }}>
              {locale.HospitalOverwriteWarning(store.hospital.Name)}
            </Body>
          </FormSection>
        )}

        <FormSection fullWidth>
          <Text label={locale["Organisation number"]} name="OrganizationNumber" />

          {store.isCustomerGroupsVisible && (
            <Select
              multi
              label={locale.CustomerGroups}
              name="CustomerGroups"
              options={store.allCustomerGroups}
            />
          )}
          <Select
            helperText={store.displayHelperText}
            label={locale.OrganizationType}
            disabled={!store.isOrgTypeSelectorEnabled}
            name="OrganizationType"
            id="OrganizationType"
            options={Object.values(OrganizationType).toLabelValue(
              (t) => locale.organizationType[t]
            )}
          />

          <Text label={locale.Name} name="Name" />

          {store.isNew && (
            <Select
              label={locale["Customer type"]}
              disabled={store.isNew}
              clearable={false}
              name="Type"
              options={Object.values(CustomerType).toLabelValue(
                (c) => locale.customerType[c]
              )}
            />
          )}

          <Collapsible fitted in={store.isRegionCustomerSelectorVisible}>
            <CustomerSelector
              disabledFilterFields={["customerType"]}
              fullWidth
              initialFilter={{ customerType: CustomerType.Region }}
              label={locale.SelectSomething(locale["Region customer"])}
              onChange={store.setRegion}
              required
              value={store.values.Region}
            />
          </Collapsible>

          <Collapsible
            in={store.isHospitalSelectorVisible}
            isLoading={store.isLoadingHospitals}
          >
            <CustomerSelector
              disabled={store.disabled}
              disabledFilterFields={["customerType"]}
              fullWidth
              initialFilter={{ customerType: CustomerType.Hospital }}
              label={locale.SelectSomething(locale.Hospital)}
              onChange={store.setHospital}
              required
              value={store.values.Hospital}
            />
          </Collapsible>

          <Select
            label={locale.Country}
            required
            name="Address.Country"
            options={store.allCountries.map((v) => ({
              label: locale.countries[v],
              value: v
            }))}
          />
          <Select
            label={locale.Region}
            required
            name="Address.Region"
            options={store.allRegions.map((c) => c.name)}
            sortOptions
          />

          {store.showMunicipalityInput && (
            <Select
              label={locale.Municipality}
              required
              name="municipalityId"
              options={store.municipalities}
            />
          )}

          <Text label={locale.City} name="Address.City" />
          <Text label={locale.StreetName} name="Address.StreetName" />
          <Text label={locale.ZipCode} name="Address.ZipCode" />

          <div className={classes.journalSystemContainer}>
            <Select
              label={locale.journalSystem}
              name="journalSystem"
              options={store.journalSystems.toLabelValue()}
            />
            <WithTooltip
              title={locale.journalSystemInfo}
              className={classes.journalSystemInfo}
            >
              <Info color="action" />
            </WithTooltip>
          </div>

          <Select
            disabled={!store.isRecruitingPortalTypeSelectorEnabled}
            label={locale.RecruitingPortalType}
            name="RecruitingPortalType"
            options={Object.values(RecruitingPortalType).toLabelValue(
              (t) => locale.recruitingPortalType[t]
            )}
          />
          <Text
            label={locale.Description}
            name="Description"
            TextFieldProps={{
              multiline: true,
              helperText: locale["Press Enter for a new line"]
            }}
          />

          {store.isOrganizationType && (
            <Text
              label={locale.billingInstructions}
              name="billingInstructions"
              TextFieldProps={{
                multiline: true,
                helperText: locale["Press Enter for a new line"]
              }}
            />
          )}
          <Text
            label={locale.bemloOrgId}
            name="bemloOrgId"
            helperText={
              store.values.Type !== CustomerType.Region
                ? locale.BemloOrgIdSetOnRegion
                : undefined
            }
            TextFieldProps={{
              disabled: store.values.Type !== CustomerType.Region
            }}
          />
          {store.values.Type !== CustomerType.Region && (
            <Text label={locale.bemloUnitId} name="bemloUnitId" />
          )}
          {store.values.Type === CustomerType.Region && (
            <Switch name={"forceCallNumber"} label={locale.ForceCallNumber} />
          )}
        </FormSection>
      </div>
    );
  };

  renderProfessionsTab = () => {
    const { store } = this.props;

    return (
      <FormTab horizontal>
        <FormSection fullWidth>
          <Grid container spacing={0}>
            <Grid item xs={12} md={12}>
              <Select
                // disabled={store.isProfessionsReadonly}
                label={locale.Professions}
                name="Professions"
                options={store.allProfessions}
                multi
                renderDescriptionInValues={false}
                sortOptions
                // sortValues
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
              <CustomerAOESettings store={store} />
            </Grid>
          </Grid>
        </FormSection>
      </FormTab>
    );
  };

  renderWorkspansTab = () => {
    const { store } = this.props;

    return (
      <FormTab horizontal>
        <FormSection fullWidth>
          <CustomerWorkshiftSpans store={store} />
        </FormSection>
      </FormTab>
    );
  };

  render() {
    const { onClose, store } = this.props;
    return (
      <TabFormLayout
        blockId="customer_form"
        canSubmit={store.canSubmit}
        isDirty={store.dirty}
        isSubmitting={store.isSubmitting}
        onSubmit={store.onSubmit}
        onReset={store.onResetHandler}
        onClose={onClose}
        tabs={[
          {
            label: <TabBadge label={locale.Main} hasErrors={!store.valid} />,
            value: "main",
            content: this.renderMainTab()
          },
          {
            disabled: store.isProfessionsTabDisabled,
            label: <TabBadge label={locale.Professions} hasErrors={false} />,
            value: "professions",
            content: this.renderProfessionsTab(),
            popoverContent: store.isProfessionsTabDisabled ? (
              <Body>{locale["Please save the form first"]}</Body>
            ) : null
          },
          {
            disabled: store.isWorkshiftsTabDisabled,
            label: <TabBadge label={locale.Workspans} hasErrors={false} />,
            value: "workspans",
            content: this.renderWorkspansTab(),
            popoverContent: store.isWorkshiftsTabDisabled ? (
              <Body>{locale["Only departments support workshifts"]}</Body>
            ) : null
          }
        ]}
      >
        <FormxDebugger store={store} />
      </TabFormLayout>
    );
  }
}

const styles = () =>
  createStyles({
    mainTab: {
      width: "50%",

      "& > *:not(:last-child)": {
        marginBottom: 16
      }
    },
    journalSystemInfo: {
      marginLeft: 5
    },
    journalSystemContainer: {
      display: "flex",
      alignItems: "center"
    }
  });

export default withStyles(styles)(CustomerForm);
